import { useEffect, useState } from 'react';
import styles from './landing-overlay.module.css';

/**
 * Daily landing splash shown once per day.
 *
 *   - On first visit of the day (per `localStorage['dashboard-landing-last-seen']`)
 *     a full-viewport teal-gradient hero is shown.
 *   - "Dive In" stamps today into localStorage and fades the overlay out over
 *     800ms, then unmounts.
 *   - On any subsequent visit the same day the overlay never renders.
 *
 * The overlay's z-index (9999) puts it above the navbar, so no navbar opacity
 * manipulation is needed.
 */

const STORAGE_KEY = 'dashboard-landing-last-seen';
const FADE_MS = 800;

type Phase = 'hidden' | 'shown' | 'fading';

function todayString(): string {
  return new Date().toDateString();
}

export default function LandingOverlay(): JSX.Element | null {
  const [phase, setPhase] = useState<Phase>('hidden');

  useEffect(() => {
    let lastSeen: string | null = null;
    try {
      lastSeen = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      // localStorage unavailable (private mode) — show overlay every visit.
    }
    if (lastSeen !== todayString()) {
      setPhase('shown');
    }
  }, []);

  if (phase === 'hidden') return null;

  const handleDiveIn = (): void => {
    try {
      window.localStorage.setItem(STORAGE_KEY, todayString());
    } catch {
      // ignore — UX still works, overlay just shows again next render
    }
    setPhase('fading');
    window.setTimeout(() => setPhase('hidden'), FADE_MS);
  };

  return (
    <div
      className={`${styles.overlay} ${phase === 'fading' ? styles.fadeOut : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="landing-title"
    >
      <div className={styles.content}>
        <h1 id="landing-title" className={styles.title}>
          Macro Allocation and Risk System
        </h1>
        <h2 className={styles.subtitle}>Advances in Systematic Macro Investing</h2>
        <p className={styles.description}>
          Advanced analytics combining real-time economic tracking, central bank
          communication analysis, and quantitative portfolio construction.
        </p>
        <button type="button" className={styles.diveIn} onClick={handleDiveIn}>
          Dive In
        </button>
      </div>
      <div className={styles.background}>
        <img
          src="https://fulcrumasset.com/wp-content/uploads/2024/08/Fulcrum-hero-spiral.svg"
          alt=""
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
